import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { 
  downloadOutline, 
  copyOutline, 
  personOutline, 
  calendarOutline,
  sparklesOutline,
  logoTwitter,
  logoFacebook,
  linkOutline
} from 'ionicons/icons';
import { Helmet } from 'react-helmet';
import { message, Spin, Tooltip } from 'antd';
import { useCopyToClipboard } from 'usehooks-ts';
import { getPublicFileById, getUserDisplayName } from '../../lib/firebase/firebase';
import './SharePage.css';

// Helper function to generate Reddit-style username from UID
const generateRedditStyleName = (uid) => {
  const adjectives = [
    'Curious', 'Wise', 'Brave', 'Swift', 'Clever', 'Bold', 'Gentle', 'Fierce', 'Noble', 'Witty',
    'Calm', 'Bright', 'Mystic', 'Silent', 'Golden', 'Silver', 'Crimson', 'Azure', 'Emerald', 'Violet',
    'Ancient', 'Modern', 'Cosmic', 'Electric', 'Frozen', 'Burning', 'Dancing', 'Flying', 'Hidden', 'Shining'
  ];
  
  const animals = [
    'Panda', 'Koala', 'Eagle', 'Wolf', 'Tiger', 'Dolphin', 'Owl', 'Fox', 'Bear', 'Lion',
    'Hawk', 'Whale', 'Deer', 'Rabbit', 'Turtle', 'Penguin', 'Falcon', 'Swan', 'Raven', 'Lynx',
    'Otter', 'Seal', 'Shark', 'Octopus', 'Phoenix', 'Dragon', 'Unicorn', 'Griffin', 'Sphinx', 'Pegasus'
  ];

  const uidHash = uid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  const adjIndex = uidHash % adjectives.length;
  const animalIndex = (uidHash * 7) % animals.length;
  const number = (uidHash % 999) + 1;
  
  return `${adjectives[adjIndex]}${animals[animalIndex]}${number}`;
};

const SharePage = () => {
  const { fileId } = useParams();
  const history = useHistory();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [artistName, setArtistName] = useState(null);
  const [, copy] = useCopyToClipboard();

  useEffect(() => {
    const fetchFile = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const fileData = await getPublicFileById(fileId);
        
        if (!fileData) {
          setError('File not found or is private');
          return;
        }
        
        setFile(fileData);
        
        // Fetch artist name
        if (fileData.ownerUid) {
          try {
            const displayName = await getUserDisplayName(fileData.ownerUid);
            setArtistName(displayName);
          } catch (e) {
            console.error('Error fetching artist name:', e);
          }
        }
      } catch (err) {
        console.error('Error fetching file:', err);
        setError('Failed to load the shared design');
      } finally {
        setLoading(false);
      }
    };

    if (fileId) {
      fetchFile();
    }
  }, [fileId]);

  const formatDate = (createdAt) => {
    if (!createdAt) return 'Unknown date';
    
    let dateObj;
    if (typeof createdAt === 'object' && createdAt.seconds) {
      dateObj = new Date(createdAt.seconds * 1000);
    } else {
      dateObj = new Date(createdAt);
    }
    
    if (isNaN(dateObj.getTime())) return 'Unknown date';
    
    return dateObj.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleDownload = async () => {
    if (!file?.url) return;
    
    try {
      const response = await fetch(file.url);
      const svgContent = await response.text();
      
      const blob = new Blob([svgContent], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `${file.seoTitle || file.name || 'svgtrace-design'}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success('SVG downloaded successfully!');
    } catch (err) {
      console.error('Error downloading:', err);
      message.error('Failed to download SVG');
    }
  };

  const handleCopyLink = () => {
    const shareUrl = window.location.href;
    copy(shareUrl);
    message.success('Link copied to clipboard!');
  };

  const handleCopyPrompt = () => {
    if (file?.prompt) {
      copy(file.prompt);
      message.success('Prompt copied to clipboard!');
    }
  };

  const handleShareTwitter = () => {
    const shareUrl = window.location.href;
    const text = file?.seoTitle 
      ? `Check out this SVG design: ${file.seoTitle}` 
      : 'Check out this awesome SVG design!';
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShareFacebook = () => {
    const shareUrl = window.location.href;
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(facebookUrl, '_blank', 'noopener,noreferrer');
  };

  const handleGoToApp = () => {
    history.push('/home');
  };

  // Generate meta tags for social sharing
  const pageTitle = file?.seoTitle 
    ? `${file.seoTitle} | SVGTrace` 
    : 'Shared Design | SVGTrace';
  
  const pageDescription = file?.seoDescription 
    || file?.prompt 
    || 'Check out this professional SVG design created with SVGTrace';

  const imageUrl = file?.publicUrl || file?.url;

  if (loading) {
    return (
      <div className="share-page">
        <div className="share-page-loading">
          <Spin size="large" />
          <p>Loading design...</p>
        </div>
      </div>
    );
  }

  if (error || !file) {
    return (
      <div className="share-page">
        <Helmet>
          <title>Design Not Found | SVGTrace</title>
        </Helmet>
        <div className="share-page-error">
          <div className="share-error-icon">🔒</div>
          <h1>Design Not Available</h1>
          <p>{error || 'This design may have been removed or made private.'}</p>
          <button className="share-cta-button" onClick={handleGoToApp}>
            Create Your Own SVG
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="share-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {imageUrl && <meta property="og:image" content={imageUrl} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      </Helmet>

      {/* Header */}
      <header className="share-header">
        <div className="share-header-content">
          <a href="/" className="share-logo">
            <img src="/assets/logo.svg" alt="SVGTrace" className="share-logo-img" />
            <span className="share-logo-text">SVGTrace</span>
          </a>
          <button className="share-header-cta" onClick={handleGoToApp}>
            <IonIcon icon={sparklesOutline} />
            Try SVGTrace Free
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="share-main">
        <div className="share-container">
          {/* Image Section */}
          <div className="share-image-section">
            <div className="share-image-wrapper">
              {!imageLoaded && (
                <div className="share-image-loading">
                  <Spin size="large" />
                </div>
              )}
              <img
                src={file.publicUrl || file.url}
                alt={file.seoTitle || 'Shared SVG Design'}
                className="share-image"
                onLoad={() => setImageLoaded(true)}
                style={{ opacity: imageLoaded ? 1 : 0 }}
              />
            </div>
          </div>

          {/* Details Section */}
          <div className="share-details-section">
            <div className="share-details-card">
              {/* Title */}
              {file.seoTitle && (
                <h1 className="share-title">{file.seoTitle}</h1>
              )}

              {/* Description */}
              {file.seoDescription && (
                <p className="share-description">{file.seoDescription}</p>
              )}

              {/* Metadata */}
              <div className="share-meta">
                {/* Artist */}
                <div className="share-meta-item">
                  <IonIcon icon={personOutline} />
                  <span>
                    Created by <strong>{artistName || (file.ownerUid ? generateRedditStyleName(file.ownerUid) : 'Anonymous')}</strong>
                  </span>
                </div>

                {/* Date */}
                <div className="share-meta-item">
                  <IonIcon icon={calendarOutline} />
                  <span>{formatDate(file.createdAt)}</span>
                </div>
              </div>

              {/* Prompt Section */}
              {file.prompt && (
                <div className="share-prompt-section">
                  <div className="share-prompt-header">
                    <span className="share-prompt-label">Original Prompt:</span>
                    <Tooltip title="Copy prompt">
                      <button className="share-copy-btn" onClick={handleCopyPrompt}>
                        <IonIcon icon={copyOutline} />
                      </button>
                    </Tooltip>
                  </div>
                  <p className="share-prompt-text">{file.prompt}</p>
                </div>
              )}

              {/* Tags */}
              {file.searchKeywords && file.searchKeywords.length > 0 && (
                <div className="share-tags">
                  {file.searchKeywords.slice(0, 8).map((tag, index) => (
                    <span key={index} className="share-tag">{tag}</span>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="share-actions">
                <button className="share-action-btn share-action-primary" onClick={handleDownload}>
                  <IonIcon icon={downloadOutline} />
                  Download SVG
                </button>
                
                <button className="share-action-btn share-action-secondary" onClick={handleCopyLink}>
                  <IonIcon icon={linkOutline} />
                  Copy Link
                </button>
              </div>

              {/* Social Share */}
              <div className="share-social">
                <span className="share-social-label">Share:</span>
                <div className="share-social-buttons">
                  <button className="share-social-btn share-twitter" onClick={handleShareTwitter}>
                    <IonIcon icon={logoTwitter} />
                  </button>
                  <button className="share-social-btn share-facebook" onClick={handleShareFacebook}>
                    <IonIcon icon={logoFacebook} />
                  </button>
                </div>
              </div>

              {/* CTA */}
              <div className="share-cta-section">
                <p className="share-cta-text">Want to create your own professional SVGs?</p>
                <button className="share-cta-button" onClick={handleGoToApp}>
                  <IonIcon icon={sparklesOutline} />
                  Try SVGTrace Free
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="share-footer">
        <p>© {new Date().getFullYear()} SVGTrace. Professional SVG conversion made simple.</p>
      </footer>
    </div>
  );
};

export default SharePage;

